import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link } from "react-router";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  getStoreAppointmentPolicy,
  configureStoreAppointmentPolicy,
  listStoreAppointmentBans,
  liftStoreAppointmentBan,
} from "@/lib/api/admin";
import type { StoreAppointmentBan } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Spinner } from "@/components/Spinner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { formatDateTime } from "@/lib/utils/date";

function banCustomerLabel(ban: StoreAppointmentBan): string {
  const name = `${ban.user_first_name ?? ""} ${ban.user_last_name ?? ""}`.trim();
  if (name) return name;
  if (ban.guest_phone) return `Guest (${ban.guest_phone})`;
  return ban.user_uid ?? "—";
}

function positiveIntOrBlank(label: string) {
  return z
    .string()
    .refine(
      (v) => v.trim() === "" || (/^\d+$/.test(v.trim()) && parseInt(v, 10) > 0),
      `${label} must be blank (platform default) or a whole number greater than 0.`
    );
}

const policySchema = z.object({
  autoBanEnabled: z.boolean(),
  cancellationWindowHours: positiveIntOrBlank("Cancellation window"),
  noShowGracePeriodMinutes: positiveIntOrBlank("No-show grace period"),
  noShowBanThreshold: positiveIntOrBlank("Ban threshold"),
  onTimeCancellationBanThreshold: positiveIntOrBlank("On-time cancellation threshold"),
  strikeResetDays: positiveIntOrBlank("Strike reset window"),
  banDurationDays: positiveIntOrBlank("Ban duration"),
});

type FormValues = z.infer<typeof policySchema>;

const BLANK: FormValues = {
  autoBanEnabled: true,
  cancellationWindowHours: "",
  noShowGracePeriodMinutes: "",
  noShowBanThreshold: "",
  onTimeCancellationBanThreshold: "",
  strikeResetDays: "",
  banDurationDays: "",
};

function toOverride(v: string): number | null {
  return v.trim() === "" ? null : parseInt(v, 10);
}

export function StoreAppointmentPolicySection({ storeId }: { storeId: string }) {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const [toLift, setToLift] = useState<StoreAppointmentBan | null>(null);

  const {
    data: policy,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["appointment-policy", storeId],
    queryFn: () => getStoreAppointmentPolicy(user!, storeId),
    enabled: !!user,
    refetchOnMount: "always",
  });

  const { data: bans } = useQuery({
    queryKey: ["appointment-bans", storeId],
    queryFn: () => listStoreAppointmentBans(user!, storeId),
    enabled: !!user,
    refetchOnMount: "always",
  });

  const liftMutation = useMutation({
    mutationFn: (b: StoreAppointmentBan) => liftStoreAppointmentBan(user!, storeId, b.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["appointment-bans", storeId] });
      setToLift(null);
      toast.success("Ban lifted.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(policySchema),
    defaultValues: BLANK,
  });

  useEffect(() => {
    if (policy) {
      reset({
        // ?? true because auto_ban_enabled is nullable on the wire: a location
        // override row leaves it unset to mean "inherit the store default".
        // Feeding null into this z.boolean() field breaks the Switch and blocks submit.
        autoBanEnabled: policy.auto_ban_enabled ?? true,
        cancellationWindowHours: policy.cancellation_window_hours?.toString() ?? "",
        noShowGracePeriodMinutes: policy.no_show_grace_period_minutes?.toString() ?? "",
        noShowBanThreshold: policy.no_show_ban_threshold?.toString() ?? "",
        onTimeCancellationBanThreshold: policy.on_time_cancellation_ban_threshold?.toString() ?? "",
        strikeResetDays: policy.strike_reset_days?.toString() ?? "",
        banDurationDays: policy.ban_duration_days?.toString() ?? "",
      });
    }
  }, [policy, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      configureStoreAppointmentPolicy(user!, storeId, {
        auto_ban_enabled: values.autoBanEnabled,
        cancellation_window_hours: toOverride(values.cancellationWindowHours),
        no_show_grace_period_minutes: toOverride(values.noShowGracePeriodMinutes),
        no_show_ban_threshold: toOverride(values.noShowBanThreshold),
        on_time_cancellation_ban_threshold: toOverride(values.onTimeCancellationBanThreshold),
        strike_reset_days: toOverride(values.strikeResetDays),
        ban_duration_days: toOverride(values.banDurationDays),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["appointment-policy", storeId] });
      toast.success("Cancellation policy updated.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const autoBanEnabled = watch("autoBanEnabled");

  if (isError)
    return (
      <Card>
        <CardContent className="py-4 text-center text-sm text-muted-foreground">
          Failed to load cancellation policy.
        </CardContent>
      </Card>
    );

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner className="size-4 border-primary" />
      </div>
    );
  }

  const fields: {
    name: keyof Pick<
      FormValues,
      | "cancellationWindowHours"
      | "noShowGracePeriodMinutes"
      | "noShowBanThreshold"
      | "onTimeCancellationBanThreshold"
      | "strikeResetDays"
      | "banDurationDays"
    >;
    label: string;
    effective?: number;
  }[] = [
    {
      name: "cancellationWindowHours",
      label: "Cancellation window (hours)",
      effective: policy?.effective_cancellation_window_hours,
    },
    {
      name: "noShowGracePeriodMinutes",
      label: "No-show grace period (minutes)",
      effective: policy?.effective_no_show_grace_period_minutes,
    },
    {
      name: "noShowBanThreshold",
      label: "Strikes before ban",
      effective: policy?.effective_no_show_ban_threshold,
    },
    {
      name: "onTimeCancellationBanThreshold",
      label: "On-time cancellations before ban",
      effective: policy?.effective_on_time_cancellation_ban_threshold,
    },
    {
      name: "strikeResetDays",
      label: "Strike reset window (days)",
      effective: policy?.effective_strike_reset_days,
    },
    {
      name: "banDurationDays",
      label: "Ban duration (days)",
      effective: policy?.effective_ban_duration_days,
    },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Cancellation Policy</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSubmit((v) => mutation.mutate(v))} noValidate className="space-y-4">
            <div className="flex items-center gap-3">
              <Controller
                control={control}
                name="autoBanEnabled"
                render={({ field }) => (
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
              <div>
                <Label>Automatically ban offenders</Label>
                <p className="text-xs text-muted-foreground">
                  When enabled, a customer is automatically banned once their strikes cross the
                  threshold below. When disabled, strikes are still tracked, but a ban must be
                  imposed manually from the Bookings tab.
                </p>
              </div>
            </div>

            {fields.map(({ name, label, effective }) => {
              const fieldReg = register(name);
              return (
                <div key={name} className="space-y-1.5">
                  <Label htmlFor={name} className="text-xs text-muted-foreground">
                    {label}
                  </Label>
                  <Input
                    id={name}
                    className="h-8 text-sm w-32"
                    type="text"
                    inputMode="numeric"
                    placeholder={effective !== undefined ? String(effective) : undefined}
                    disabled={name !== "cancellationWindowHours" && !autoBanEnabled}
                    {...fieldReg}
                    onChange={(e) => {
                      e.target.value = e.target.value.replace(/[^\d]/g, "");
                      fieldReg.onChange(e);
                    }}
                  />
                  {effective !== undefined && (
                    <p className="text-xs text-muted-foreground">
                      Blank uses the platform default ({effective}).
                    </p>
                  )}
                  {errors[name] && (
                    <p className="text-xs text-destructive">{errors[name]?.message}</p>
                  )}
                </div>
              );
            })}

            <Button type="submit" size="sm" disabled={mutation.isPending || !isDirty}>
              {mutation.isPending ? (
                <Spinner className="size-3.5 border-primary-foreground" />
              ) : (
                "Save Changes"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Active Bans ({bans?.length ?? 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {!bans || bans.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No active bans at this store.
            </p>
          ) : (
            <ul className="divide-y">
              {bans.map((b) => (
                <li key={b.id} className="flex items-center justify-between gap-2 py-2">
                  <div className="min-w-0">
                    {b.user_uid ? (
                      <Link
                        to={`/users/${b.user_uid}`}
                        className="text-sm font-medium hover:underline"
                      >
                        {banCustomerLabel(b)}
                      </Link>
                    ) : (
                      <p className="text-sm font-medium">{banCustomerLabel(b)}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Until {formatDateTime(b.banned_until)}
                      {b.reason && ` · ${b.reason}`}
                      {b.imposed_by ? " · Manual" : " · Automatic"}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    onClick={() => setToLift(b)}
                  >
                    Lift
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!toLift}
        onOpenChange={(o) => {
          if (!o) setToLift(null);
        }}
        title="Lift booking ban"
        description={`Lift ${toLift ? banCustomerLabel(toLift) : "this customer"}'s ban? They will be able to book appointments at this store again.`}
        confirmLabel="Lift Ban"
        isPending={liftMutation.isPending}
        onConfirm={() => toLift && liftMutation.mutate(toLift)}
      />
    </div>
  );
}
